"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { BrandNewVariantProps } from "./props";
import { ChevronLeft, ChevronRight, Maximize } from "lucide-react";
import useDeviceType from "@/components/package-components/hooks/useDeviceType";
import Caroussel from "../../carroussel";
import { PlatformsImage } from "@/components/package-components/non-editable/core";
import styles from "./beehive.module.scss";

export default function BrandNewGaleryVariant({
  vehicle,
  vehicleImagesLarge,
  openLightboxOnSlide,
  internalImagesThumb,
  showInternalImages,
  setShowInternalImages,
}: BrandNewVariantProps) {
  const { isMobile } = useDeviceType();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [vehicleImages, setVehicleImages] =
    useState<string[]>(vehicleImagesLarge);

  useEffect(() => {
    if (showInternalImages && internalImagesThumb) {
      setVehicleImages(internalImagesThumb);
    } else {
      setVehicleImages(vehicleImagesLarge);
    }
  }, [showInternalImages, internalImagesThumb, vehicleImagesLarge]);

  const nextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % vehicleImages.length);
  };

  const prevImage = () => {
    setCurrentIndex(
      (prevIndex) =>
        (prevIndex - 1 + vehicleImages.length) % vehicleImages.length
    );
  };

  const goToImage = (index: number) => {
    setCurrentIndex(index);
  };

  const handleImageClick = () => {
    openLightboxOnSlide(currentIndex + 1);
  };

  const handleInternalImagesToggle = () => {
    if (setShowInternalImages) {
      setShowInternalImages(!showInternalImages);
    }
  };

  if (isMobile) {
    return (
      <Caroussel
        itemsPerView="1"
        itens={vehicleImages.map((i, idx) => (
          <PlatformsImage
            width={600}
            height={500}
            onClick={() =>
              openLightboxOnSlide(vehicleImagesLarge.length > idx ? idx + 1 : 1)
            }
            src={
              vehicleImagesLarge.length > idx
                ? vehicleImagesLarge[idx]
                : vehicleImagesLarge[0]
            }
            alt={`Imagem exterior do veículo ${vehicle.model} ${vehicle.modelYear} ${vehicle.version} `}
            className={styles.image}
            preload={idx < 1}
            priority={idx < 1}
            placeholder="blur"
            blurDataURL="https://sync-front-end-resources.s3.sa-east-1.amazonaws.com/blur-image.jpg"
          />
        ))}
      />
    );
  }

  return (
    <div className="relative w-full group">
      <div>
        <button
          onClick={handleImageClick}
          className="absolute top-6 right-2 transform -translate-y-1/2 bg-white p-2 rounded-lg shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Visualizar Imagem em Tela Cheia"
        >
          <Maximize strokeWidth={1.5} />
        </button>
      </div>
      <div className="overflow-hidden rounded-lg w-full">
        <Image
          src={vehicleImages[currentIndex]}
          alt={`Imagem ${currentIndex} do veiculo ${vehicle.make} ${vehicle.model} - ${vehicle.version}`}
          width={100}
          height={1600}
          className="w-full h-auto rounded-lg transition-transform duration-1000 ease-in-out"
          onClick={handleImageClick}
          placeholder="blur"
          blurDataURL="https://sync-front-end-resources.s3.sa-east-1.amazonaws.com/blur-image.jpg"
        />
      </div>
      <button
        onClick={prevImage}
        className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Imagem Anterior"
      >
        <ChevronLeft strokeWidth={1.5} />
      </button>
      <button
        onClick={nextImage}
        className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Próxima Imagem"
      >
        <ChevronRight strokeWidth={1.5} />
      </button>
      <div className="flex justify-center items-center mt-4 bg-white rounded-full absolute bottom-2 left-1/2 transform -translate-x-1/2 px-1 py-1 h-5 opacity-0 group-hover:opacity-100 transition-opacity">
        {vehicleImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToImage(index)}
            className={`bg-gray-400 mx-2 rounded-full transition-all duration-300 ease-in-out ${
              currentIndex === index ? "w-3 h-3" : "w-2 h-2"
            }`}
            aria-label={`Imagem ${index + 1}`}
          />
        ))}
      </div>
      <div className="absolute bottom-2 left-2 flex flex-row overflow-hidden rounded-full opacity-0 group-hover:opacity-100 bg-white shadow-md">
        <button
          onClick={handleInternalImagesToggle}
          className={`px-4 text-[.7rem] transition-colors duration-200 pt-0.5 ${
            !showInternalImages
              ? "bg-gray-200 text-gray-400 shadow-inner"
              : "bg-white text-black shadow"
          }`}
        >
          Interior
        </button>
        <button
          onClick={handleInternalImagesToggle}
          className={`px-4 text-[.7rem] transition-colors duration-200 pt-0.5 ${
            showInternalImages
              ? "bg-gray-200 text-gray-400 shadow-inner"
              : "bg-white text-black shadow"
          }`}
        >
          Exterior
        </button>
      </div>
    </div>
  );
}
